import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";

export interface CountryAuthConfig {
  code: string;
  /** "firebase" | "bulkgate" | "twilio" | "disabled" */
  otpPhoneProvider: string;
  /** "sendgrid" | "postmark" | "disabled" */
  otpEmailProvider: string;
  phonePrefix: string;
  firebaseClientConfig: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
  } | null;
}

/**
 * Country-scoped auth provider config. Drives which OTP channels the login
 * modal exposes per residence country (some markets disable SMS to keep
 * Firebase / BulkGate spend down, others ship email magic-link only).
 *
 * Endpoint is public + cached server-side (Cache-Control: 1h), and we add
 * a 1h client-side stale window so a country switch doesn't refetch when
 * the user toggles between residence and phone-prefix in the modal.
 */
export function useCountryConfig(country: string | null | undefined) {
  return useQuery<CountryAuthConfig>({
    queryKey: ["auth", "country-config", country?.toUpperCase() ?? ""],
    queryFn: async () =>
      apiFetch.get<CountryAuthConfig>(
        `/api/auth/country-config?country=${encodeURIComponent((country ?? "RO").toUpperCase())}`,
        { skipAuthRefresh: true },
      ),
    enabled: typeof country === "string" && country.length === 2,
    staleTime: 60 * 60_000,
    gcTime: 24 * 60 * 60_000,
  });
}

/** Quick boolean accessors for common gates. */
export function isPhoneOtpEnabled(cfg: CountryAuthConfig | undefined): boolean {
  if (!cfg) return true; // optimistic — assume enabled until probe completes
  return cfg.otpPhoneProvider !== "disabled";
}

export function isEmailOtpEnabled(cfg: CountryAuthConfig | undefined): boolean {
  if (!cfg) return true;
  return cfg.otpEmailProvider !== "disabled";
}
